import React, { useState, useEffect, useRef } from 'react';
import { fetchAllCompanies, searchCompanies } from '../services/apiService';
import { FaSearch, FaBuilding, FaSpinner } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';

const extractCompaniesArray = (data) => {
  if (Array.isArray(data?.data)) {
    return data.data;
  } else if (data?.data && Array.isArray(data.data.companies)) {
    return data.data.companies;
  } else if (Array.isArray(data?.companies)) {
    return data.companies;
  } else if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const CompanyDropdown = ({ onSelect, selectedCompany }) => {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setLoading(true);
    fetchAllCompanies()
      .then(data => setCompanies(extractCompaniesArray(data)))
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setLoading(true);
    if (value.trim() === '') {
      fetchAllCompanies()
        .then(data => setCompanies(extractCompaniesArray(data)))
        .finally(() => setLoading(false));
    } else {
      searchCompanies(value)
        .then(data => setCompanies(extractCompaniesArray(data)))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="relative w-80" ref={dropdownRef}>
      {/* Floating label and button */}
      <button
        className={`w-full flex items-center gap-3 px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl min-w-[180px] text-left focus:outline-none ${
          isOpen 
            ? 'bg-gradient-to-r from-indigo-600 to-purple-700 text-white shadow-indigo-200' 
            : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
        }`}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <FaBuilding className={`text-lg ${isOpen ? 'text-white' : 'text-indigo-500'}`} />
        <span className={`text-base font-medium ${isOpen ? 'text-white' : (selectedCompany ? 'text-gray-900' : 'text-gray-400')}`}>
          {selectedCompany || t('filters.company.placeholder')}
        </span>
        {isOpen && (
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        )}
        <svg className={`ml-auto h-4 w-4 ${isOpen ? 'text-white' : 'text-gray-400'} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-full bg-white border-2 border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden animate-fade-in">
          {/* Search input */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
            <FaSearch className="text-gray-400 text-base" />
            <input
              ref={inputRef}
              type="text"
              className="w-full bg-transparent outline-none text-base placeholder-gray-400"
              placeholder={t('filters.company.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearch}
            />
            {loading && <FaSpinner className="animate-spin text-indigo-500 text-base ml-2" />}
          </div>
          {/* Companies list */}
          <div className="max-h-64 overflow-y-auto custom-scrollbar bg-white">
            {companies.length > 0 ? (
              companies.map((company) => {
                const companyName = typeof company === 'string' ? company : company.name;
                return (
                  <div
                    key={company._id || company.name || companyName}
                    className={`flex items-center gap-2 px-4 py-3 cursor-pointer transition-all duration-150 text-base hover:bg-indigo-50 ${selectedCompany === companyName ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-700'}`}
                    onClick={() => {
                      onSelect(companyName);
                      setIsOpen(false);
                    }}
                  >
                    <FaBuilding className="text-indigo-400" />
                    <span className="truncate">{companyName}</span>
                    {selectedCompany === companyName && (
                      <svg className="ml-auto h-4 w-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-6 text-center text-gray-400 text-base">{t('filters.company.noCompaniesFound')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDropdown; 